import { useEffect } from 'react'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'

interface HotkeysModalProps {
  onClose: () => void
}

/** One row of the keycap list: the key(s) on the left, what they do on the right. */
interface Hotkey {
  /** Rendered as individual <kbd> caps. */
  keys: string[]
  /** Joiner shown between caps (e.g. "or", "+"). Defaults to no joiner. */
  joiner?: string
  description: string
}

interface HotkeyGroup {
  title: string
  hotkeys: Hotkey[]
}

/**
 * Currently-implemented VTT hotkeys, grouped. Source of truth is
 * docs/hotkeys.md "Implemented" section — only real binds live here.
 * Do NOT add proposed-but-unbuilt keys; the footnote covers those.
 */
const HOTKEY_GROUPS: HotkeyGroup[] = [
  {
    title: 'Selection',
    hotkeys: [
      { keys: ['Shift'], description: 'Add to selection' },
      {
        keys: ['Esc'],
        description: 'Deselect · cancel marquee · cancel barrier placement',
      },
    ],
  },
  {
    title: 'Tokens',
    hotkeys: [
      { keys: ['↑', '↓', '←', '→'], description: 'Nudge selected token(s) by one grid cell' },
      {
        keys: ['Delete', 'Backspace'],
        joiner: 'or',
        description: 'Delete selected token(s) — GM any, player own',
      },
      { keys: ['Enter'], description: 'Confirm inline rename / HP edit' },
    ],
  },
  {
    title: 'GM tools',
    hotkeys: [{ keys: ['B'], description: 'Toggle barrier tool (GM only)' }],
  },
]

/**
 * v5 hotkeys reference modal — vtt-modal-* chrome shared with the map
 * and token editors. Opened from the Settings tab; read-only keycap list.
 */
export function HotkeysModal({ onClose }: HotkeysModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div className="vtt-modal-backdrop" onClick={onClose}>
      <div
        className="vtt-modal-surface"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 440 }}
      >
        <div className="vtt-modal-header">
          <div>
            <div className="ac-panel-eyebrow">Reference</div>
            <div className="ac-panel-title">Keyboard shortcuts</div>
          </div>
          <IconButton size="sm" variant="ghost" aria-label="Close" onClick={onClose}>
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </div>

        <div className="vtt-modal-body">
          {HOTKEY_GROUPS.map((group) => (
            <div key={group.title}>
              <div
                className="ac-panel-eyebrow"
                style={{ marginBottom: '0.5rem' }}
              >
                {group.title}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                {group.hotkeys.map((hotkey) => (
                  <div
                    key={hotkey.description}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        flexShrink: 0,
                        minWidth: '6.5rem',
                      }}
                    >
                      {hotkey.keys.map((key, i) => (
                        <span
                          key={key}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                          {i > 0 && hotkey.joiner && (
                            <span
                              style={{
                                fontSize: '0.625rem',
                                color: 'hsl(var(--ac-text-muted))',
                              }}
                            >
                              {hotkey.joiner}
                            </span>
                          )}
                          <kbd className="vtt-keycap">{key}</kbd>
                        </span>
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: '0.8125rem',
                        color: 'hsl(var(--ac-text-muted))',
                        lineHeight: 1.4,
                      }}
                    >
                      {hotkey.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div
            className="ac-help"
            style={{
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
              opacity: 0.7,
              paddingTop: '0.25rem',
              borderTop: '1px solid hsl(var(--ac-dark-accent))',
            }}
          >
            More shortcuts coming.
          </div>
        </div>
      </div>
    </div>
  )
}
